import { Component, OnDestroy, OnInit } from '@angular/core';
import * as cds from '@cds/core/icon';
import '@cds/core/icon/register.js';
import { TranslateService } from '@ngx-translate/core';
import { ProductCatalogService } from './services/product-catalog.service';
import { ThemeProcessorService } from './services/theme-processor.service';
import { NavigationEnd, Router } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { LocalStorage, RegistrationTool, Segment } from './models/constants';
import { MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { AuthService } from './services/authentication/auth.service';
import { UserService } from './shared/services/user.service';
import { SalesContextService } from './services/sales-context.service';
import { AppConfig } from './shared/utils/config/app-config';

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
	private readonly destroy$ = new Subject<void>();

	constructor(
		public translate: TranslateService,
		private productCatalogService: ProductCatalogService,
		private themeProcessor: ThemeProcessorService,
		private router: Router,
		private msalBroadcastService: MsalBroadcastService,
		private authService: AuthService,
		private userService: UserService,
		private salesContextService: SalesContextService,
		private config: AppConfig
	) {
		this.translate.addLangs(['nl', 'fr', 'en']);
		this.translate.setDefaultLang('fr');

		if (localStorage.getItem('language') != null) {
			this.translate.use(localStorage.getItem('language'));
		} else {
			this.translate.use('fr');
		}

		if (localStorage.getItem(LocalStorage.MARKET) != null && localStorage.getItem(LocalStorage.TOOLS) != null) {
			this.refreshTheme();
		}

		cds.loadCoreIconSet();
		cds.loadEssentialIconSet();
		cds.loadCommerceIconSet();
		cds.loadMediaIconSet();
		cds.loadSocialIconSet();
		cds.loadTravelIconSet();
		cds.loadTextEditIconSet();
		cds.loadTechnologyIconSet();
		cds.loadChartIconSet();

		document
			.querySelector('meta[name=viewport]')
			.setAttribute('content', 'width=device-width, initial-scale=' + 1 / window.devicePixelRatio + ', shrink-to-fit=no');

		this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
			if (event.url.includes(`/${RegistrationTool.MOVE}`)) {
				localStorage.setItem(LocalStorage.TOOLS, RegistrationTool.MOVE);
			} else if (event.url.includes(`/${RegistrationTool.REGISTRATIONS}`)) {
				localStorage.setItem(LocalStorage.TOOLS, RegistrationTool.REGISTRATIONS);
			}
			if (event.url.includes(`${Segment.B2C}`)) {
				localStorage.setItem(LocalStorage.MARKET, Segment.B2C);
			} else if (event.url.includes(`${Segment.B2B}`)) {
				localStorage.setItem(LocalStorage.MARKET, Segment.B2B);
			}
			this.refreshTheme();
		});
	}

	/**
	 * Any protected/backend HTTP call (bundles, sales contexts, pipeline data) MUST run only
	 * once MSAL interaction has settled (`InteractionStatus.None`) — never eagerly at
	 * bootstrap/constructor time. See cognito-to-msal-migration skill, lesson #6: an early
	 * protected call can leave a stale `interaction_in_progress` lock that silently blocks
	 * the real login redirect triggered later by `MsalGuard`.
	 */
	ngOnInit(): void {

		this.msalBroadcastService.inProgress$
			.pipe(
				filter((status: InteractionStatus) => status === InteractionStatus.None),
				takeUntil(this.destroy$)
			)
			.subscribe(() => {
				this.authService.initCurrentUser();
				const activeAccount = this.authService.getActiveAccount();
				if (activeAccount) {
					this.userService.setUserFromMsal(activeAccount);
					this.productCatalogService.loadBundles();
					this.salesContextService.loadSalesContexts();
					if (this.config.instance?.production) {
						this.config.loadPipelineData();
					}
				}
			});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private refreshTheme() {
		this.themeProcessor.changeTheme(
			localStorage.getItem(LocalStorage.MARKET).toLowerCase(),
			localStorage.getItem(LocalStorage.TOOLS).toLowerCase()
		);
	}
}
